import {SxProps, Theme} from '@mui/material';
import theme from 'src/theme';

export const cardSx: SxProps<Theme> = {
    background: theme.palette.common.white,
    borderRadius: '14px',
    border: '1px solid rgba(0, 0, 0, 0.05)',
    boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 4px 12px rgba(0,0,0,0.04)',
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
};

export const headerSx: SxProps<Theme> = {
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    px: 2.5,
    py: 2,
};

export const badgeSx: SxProps<Theme> = {
    width: '32px',
    height: '32px',
    borderRadius: '8px',
    background: `linear-gradient(135deg, ${theme.palette.primaryColor[50]} 0%, ${theme.palette.primaryColor[100]} 100%)`,
    color: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    '& svg': {fontSize: '18px'},
};

export const titleSx: SxProps<Theme> = {
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.primary.main,
};

/** The rows share the height the row of cards asks for, so this card ends level with the one beside it. */
export const rowsSx: SxProps<Theme> = {
    flex: '1 1 auto',
    display: 'flex',
    flexDirection: 'column',
};

export const rowSx: SxProps<Theme> = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 1.25,
    px: 2.5,
    py: 1.75,
    borderTop: `1px solid ${theme.palette.neutral[100]}`,
    minWidth: 0,
};

/**
 * The row's mark.
 *
 * Tinted when the row points somewhere, plain grey when it does not — so an empty row still has its
 * shape, its label and its rhythm, and reads as "nothing linked" rather than as a missing row. The
 * card keeps its height either way, which is the whole point of a rail beside a card of facts.
 */
export const rowBadgeSx = (linked: boolean, accent?: 'brand'): SxProps<Theme> => ({
    width: '30px',
    height: '30px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    background: linked
        ? (accent === 'brand' ? '#FEF3F6' : theme.palette.primaryColor.A50)
        : theme.palette.neutral[50],
    color: linked
        ? (accent === 'brand' ? theme.palette.brand.main : theme.palette.primary.main)
        : theme.palette.neutral[400],
    border: `1px solid ${linked
        ? (accent === 'brand' ? '#F7DDE4' : theme.palette.primaryColor.A100)
        : theme.palette.neutral[100]}`,
    '& svg': {fontSize: '17px'},
});

export const rowLabelSx: SxProps<Theme> = {
    fontSize: '0.625rem',
    fontWeight: 700,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.palette.neutral[500],
};

export const rowValueSx = (accent?: 'brand'): SxProps<Theme> => ({
    fontSize: '0.875rem',
    fontWeight: 500,
    color: accent === 'brand' ? theme.palette.brand.dark : theme.palette.primary.dark,
    textDecoration: 'none',
    display: 'inline-block',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '100%',
    '&:hover': {textDecoration: 'underline'},
});

/** A value that is not a link — a count, a mode, a plain name. */
export const rowPlainSx: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 500,
};

export const rowEmptySx: SxProps<Theme> = {
    fontSize: '0.875rem',
    fontWeight: 400,
    fontStyle: 'italic',
    color: theme.palette.neutral[400],
};

export const rowNoteSx: SxProps<Theme> = {
    fontSize: '0.75rem',
    color: theme.palette.neutral[500],
    fontWeight: 400,
};
